/**
 * NotesModule — standalone plain-text Notes module (replaces the legacy
 * drive/notes implementation). Two-pane master/detail: a reverse-chronological
 * note rail (pinned group on top) and a borderless plain-text editor with
 * 800ms autosave. Postgres is the source of truth via the generic /notes
 * objects API; the Drive .txt mirror (Phase 5) hangs off attributes.contentHash.
 *
 * Invariants (docs/design/notes-conventions.md): plain text only, no rich text,
 * no folders, no tags, flat namespace.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { apiPost } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/keys';
import { useSession } from '@/features/auth/hooks/useSession';
import { useToast } from '@/app/providers/ToastContext';
import { useObjectsByType, useCreateObject, useUpdateObject } from '@/features/objects/hooks';
import type { Module } from '@/types/modules';
import NoteList from './components/NoteList';
import NoteEditor from './components/NoteEditor';
import { useNoteAutoSave } from './hooks/useNoteAutoSave';
import {
  byUpdatedDesc,
  isArchived,
  needsRestoreBeforeDelete,
  shouldCreateFromDraft,
  titleForPersist,
  NEW_NOTE_SENTINEL,
  type NoteObject,
} from './notesShared';
import '../_infrastructure/ModuleStyles.css'; // shared .module-toolbar-button + design tokens
import './NotesModule.css';

interface NotesModuleProps {
  module: Module;
  conversationId?: string;
  filterText?: string | null;
  [legacyProp: string]: unknown;
}

export default function NotesModule({ module, conversationId }: NotesModuleProps) {
  const moduleId = module.id;
  const qc = useQueryClient();
  const { token } = useSession();
  const { showToast } = useToast();

  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  // The note id the current `draft` belongs to — gates autosave so we never
  // persist a draft against the wrong note during a selection switch.
  const [draftNoteId, setDraftNoteId] = useState<string | null>(null);
  // Pending-new state: an empty, editable editor that is NOT yet backed by a
  // persisted object. No note is created until the first non-whitespace
  // keystroke (handleDraftChange), so clicking "+ New" without typing — or
  // leaving the draft — creates nothing.
  const [pendingNew, setPendingNew] = useState(false);
  // One-shot guard so the first-keystroke create fires exactly once.
  const creatingRef = useRef(false);

  // One query covering active + archived. The list response carries
  // `archivedAt`, so we partition client-side via isArchived — no second query
  // or set-difference (which was fragile across refetch windows and silently
  // capped at the page limit).
  const notesQuery = useObjectsByType<NoteObject>('note', { moduleId, includeArchived: true, limit: 500 });
  const isLoading = notesQuery.isLoading;
  const allNotes = useMemo(() => (notesQuery.data?.items ?? []) as NoteObject[], [notesQuery.data]);
  const activeNotes = useMemo(() => allNotes.filter((n) => !isArchived(n)), [allNotes]);
  const archivedNotes = useMemo(() => allNotes.filter((n) => isArchived(n)), [allNotes]);

  const visibleNotes = useMemo(() => {
    const pool = showArchived ? archivedNotes : activeNotes;
    const q = query.trim().toLowerCase();
    const filtered = q
      ? pool.filter((n) => `${n.body ?? ''} ${n.title ?? ''}`.toLowerCase().includes(q))
      : pool;
    return [...filtered].sort(byUpdatedDesc);
  }, [showArchived, archivedNotes, activeNotes, query]);

  const activeNote = useMemo(
    () => allNotes.find((n) => n.id === activeId) ?? null,
    [allNotes, activeId],
  );
  const activeNoteArchived = useMemo(() => Boolean(activeNote && isArchived(activeNote)), [activeNote]);

  // Seed the editor draft from the note's server body. On a note switch, load
  // it fresh. While staying on the same note, adopt an EXTERNAL body change
  // (e.g. the agent edited the open note) only when the user has no unsaved
  // local edits; keep the ref in sync once the server catches up to the draft.
  const loadedBodyRef = useRef<string>('');
  useEffect(() => {
    if (!activeNote) return;
    const serverBody = activeNote.body ?? '';
    if (draftNoteId !== activeNote.id) {
      setDraftNoteId(activeNote.id);
      setDraft(serverBody);
      loadedBodyRef.current = serverBody;
      return;
    }
    if (serverBody === draft) {
      loadedBodyRef.current = serverBody; // server caught up to the draft
    } else if (draft === loadedBodyRef.current) {
      setDraft(serverBody); // external change, no local edits → adopt it
      loadedBodyRef.current = serverBody;
    }
    // else: user has unsaved local edits — keep the draft (autosave's 412 retry
    // handles genuine concurrent-edit conflicts).
  }, [activeNote, draftNoteId, draft]);

  // Keep a valid selection: pick the first visible note when nothing is
  // selected or the selection left the current view (archive/delete/toggle).
  // Skipped while a pending-new draft is open — the sentinel id intentionally
  // matches no note, and the empty editor must stay put until the user types.
  useEffect(() => {
    if (pendingNew) return;
    if (visibleNotes.length === 0) {
      if (activeId !== null) setActiveId(null);
      return;
    }
    if (!activeId || !visibleNotes.some((n) => n.id === activeId)) {
      setActiveId(visibleNotes[0].id);
    }
  }, [visibleNotes, activeId, pendingNew]);

  // Toggling the archived view abandons any pending-new draft (persists nothing).
  const toggleArchived = useCallback(() => {
    setPendingNew(false);
    creatingRef.current = false;
    setShowArchived((p) => !p);
  }, []);

  // Header "View Archived" menu item toggles the same state as the rail button.
  useEffect(() => {
    const handler = (event: Event) => {
      if ((event as CustomEvent).detail?.moduleId === moduleId) toggleArchived();
    };
    window.addEventListener('xavior:toggle-archived-notes', handler as EventListener);
    return () => window.removeEventListener('xavior:toggle-archived-notes', handler as EventListener);
  }, [moduleId, toggleArchived]);

  const { status, savedAt } = useNoteAutoSave({ note: activeNote, draft, draftNoteId, moduleId });

  const createNote = useCreateObject<{ title: string; body: string; moduleLinks: { moduleId: string }[] }, NoteObject>(
    'note',
    conversationId,
    moduleId,
  );
  const updateNote = useUpdateObject<NoteObject>('note', undefined, moduleId);

  const invalidateNotes = useCallback(
    () => qc.invalidateQueries({ queryKey: queryKeys.objects.byType('note') }),
    [qc],
  );

  // Enter pending-new state: an empty, focused editor that persists NOTHING
  // until the user's first non-whitespace keystroke (see handleDraftChange).
  const handleNew = useCallback(() => {
    if (showArchived) setShowArchived(false);
    setQuery('');
    creatingRef.current = false;
    setPendingNew(true);
    setDraftNoteId(null);
    setDraft('');
    setActiveId(NEW_NOTE_SENTINEL);
  }, [showArchived]);

  // Body changes flow through here so the first real keystroke of a pending-new
  // draft creates the note exactly once; thereafter useNoteAutoSave persists.
  const handleDraftChange = useCallback(
    (body: string) => {
      setDraft(body);
      if (!pendingNew || creatingRef.current || !shouldCreateFromDraft(body)) return;
      creatingRef.current = true;
      void (async () => {
        try {
          const created = await createNote.mutateAsync({
            title: titleForPersist(body),
            body,
            moduleLinks: [{ moduleId }],
          });
          await invalidateNotes();
          setPendingNew(false);
          setDraftNoteId(created.id);
          setActiveId(created.id);
        } catch {
          // Allow a retry on the next keystroke if the create failed.
          creatingRef.current = false;
          showToast('Could not create note', 'error');
        }
      })();
    },
    [pendingNew, createNote, moduleId, invalidateNotes, showToast],
  );

  // Selecting another note (or otherwise changing selection) abandons a
  // pending-new draft without persisting anything.
  const handleSelect = useCallback((id: string) => {
    setPendingNew(false);
    creatingRef.current = false;
    setActiveId(id);
  }, []);

  const handleTogglePin = useCallback(async () => {
    if (!activeNote) return;
    const etag = activeNote._etag || String(activeNote.version ?? '');
    try {
      await updateNote.mutateAsync({ id: activeNote.id, etag, patch: { pinned: !activeNote.pinned } });
    } catch {
      showToast('Could not update pin', 'error');
    }
  }, [activeNote, updateNote, showToast]);

  const handleToggleArchive = useCallback(async () => {
    if (!activeNote) return;
    const wasArchived = activeNoteArchived;
    try {
      if (wasArchived) {
        await apiPost(`/notes/${activeNote.id}/restore`, {});
        showToast('Note restored', 'success');
      } else {
        await api.objects.deleteObject(token, 'note', activeNote.id, false);
        showToast('Note archived', 'success');
      }
      setActiveId(null);
      await invalidateNotes();
    } catch {
      showToast(wasArchived ? 'Could not restore note' : 'Could not archive note', 'error');
    }
  }, [activeNote, activeNoteArchived, token, invalidateNotes, showToast]);

  const handleDelete = useCallback(async () => {
    if (!activeNote) return;
    if (!window.confirm('Delete this note permanently? This cannot be undone.')) return;
    try {
      // A permanent delete of an archived note 404s: archived objects are
      // excluded from the ACL scope the DELETE handler checks. Restore it first
      // (which brings it back into scope), then permanently delete.
      if (needsRestoreBeforeDelete(activeNote)) {
        await apiPost(`/notes/${activeNote.id}/restore`, {});
      }
      await api.objects.deleteObject(token, 'note', activeNote.id, true);
      showToast('Note deleted', 'success');
      setActiveId(null);
      await invalidateNotes();
    } catch {
      showToast('Could not delete note', 'error');
    }
  }, [activeNote, token, invalidateNotes, showToast]);

  // Synthetic, unpersisted note backing the pending-new editor. The autosave
  // hook is passed note=null while pending (so it no-ops); this placeholder is
  // for presentation only and is replaced by the real note on first keystroke.
  const editorNote: NoteObject | null = activeNote
    ? activeNote
    : pendingNew
      ? { id: NEW_NOTE_SENTINEL, type: 'note' as NoteObject['type'], createdAt: '', updatedAt: '', version: 0, body: '' }
      : null;

  return (
    <div className="notes-module">
      <NoteList
        notes={visibleNotes}
        activeId={activeId}
        activeDraft={draft}
        onSelect={handleSelect}
        query={query}
        onQueryChange={setQuery}
        showArchived={showArchived}
        onToggleArchived={toggleArchived}
        onNew={handleNew}
        activeCount={activeNotes.length}
        archivedCount={archivedNotes.length}
        isLoading={isLoading}
      />
      {editorNote ? (
        <NoteEditor
          note={editorNote}
          archived={activeNoteArchived}
          draft={draft}
          onDraftChange={handleDraftChange}
          status={status}
          savedAt={savedAt}
          onTogglePin={handleTogglePin}
          onToggleArchive={handleToggleArchive}
          onDelete={handleDelete}
        />
      ) : (
        <section className="notes-editor">
          <div className="notes-no-sel">
            <div className="notes-no-sel-glyph">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M4 5h16M4 12h16M4 19h10" />
              </svg>
            </div>
            <p>Select a note, or start a new one. You can also just tell Xavior to jot something down.</p>
          </div>
        </section>
      )}
    </div>
  );
}
